import React from "react";
import { useGetReviewManifestationsQuery } from "../../core/dbc-gateway/generated/graphql";
import { ReviewManifestation } from "../../core/utils/types/entities";
import { Pid } from "../../core/utils/types/ids";
import ReviewExternal from "./ReviewExternal";
import ReviewRetriever from "./ReviewRetriever";
import ReviewLibrarian from "./ReviewLibrarian";
import ReviewSkeleton from "./ReviewSkeleton";

export interface MaterialReviewsProps {
  pids: Pid[];
  dataCy?: string;
}

export const MaterialReviews: React.FC<MaterialReviewsProps> = ({
  pids,
  dataCy = "material-reviews"
}) => {
  const { data, isLoading } = useGetReviewManifestationsQuery({
    pid: pids
  });

  if (isLoading) {
    return <ReviewSkeleton />;
  }

  if (!data) {
    return null;
  }

  const { manifestations: reviews } = data;

  const getReviewType = (review: ReviewManifestation) => {
    if (
      review?.access.some((access) => access.__typename === "RetrieverService")
    ) {
      return "retriever";
    }
    if (review?.access.some((access) => access.__typename === "AccessUrl")) {
      return "external";
    }
    // Only classify as librarian if there's actual review content in reviewByLibrarians array.
    if (review?.review?.reviewByLibrarians?.length) {
      return "librarian";
    }
    // Reviews with InterLibraryLoan access but no librarian content are external reviews
    if (
      review?.access.some((access) => access.__typename === "InterLibraryLoan")
    ) {
      return "external";
    }
    return null;
  };

  const chooseReview = (
    reviewType: "retriever" | "external" | "librarian" | null,
    review: ReviewManifestation
  ) => {
    switch (reviewType) {
      case null:
        return null;
      case "retriever":
        return (
          <ReviewRetriever review={review} dataCy={`${dataCy}-retriever`} />
        );
      case "external":
        return <ReviewExternal review={review} dataCy={`${dataCy}-external`} />;
      case "librarian":
        return (
          <ReviewLibrarian review={review} dataCy={`${dataCy}-librarian`} />
        );
      default:
        return null;
    }
  };

  return (
    <ul className="reviews" data-cy={dataCy}>
      {reviews.map((review) => {
        if (!review) {
          return null;
        }
        return chooseReview(
          getReviewType(review as ReviewManifestation),
          review as ReviewManifestation
        );
      })}
    </ul>
  );
};

export default MaterialReviews;
